import * as React from "react";
import { Link } from "gatsby";
import {
  formatNumberDisplay,
  formatNumber,
} from "../../../../../../../../utils/format-number";
import { LatestCovidCasesResult } from "../../../../../../../../types/latest-covid-cases";
import { Colors } from "../../../../../../../design-tokens/colors";
import CardSubHeading from "../../../../../../../ui-library/content-card/card-sub-heading";
import { getCountryUrl } from "../../../../../../../../utils/question-url";
import {
  getEventAttributes,
  EventCategory,
} from "../../../../../../../../utils/analytics";
import styles from "./rank-table.styles";

interface RankDeathCasesProps {
  latestCovidCases?: LatestCovidCasesResult;
}

const RankDeathCases: React.FC<RankDeathCasesProps> = ({
  latestCovidCases,
}) => {
  const [isDesc, setDesc] = React.useState<boolean>(true);
  const color = Colors.Red;

  const sortedCountries = latestCovidCases
    ?.sort((a, b) => {
      if (!isDesc) {
        return (a.totalDeathsPerMillion || 0) - (b.totalDeathsPerMillion || 0);
      } else {
        return (b.totalDeathsPerMillion || 0) - (a.totalDeathsPerMillion || 0);
      }
    })
    .slice(0, 8);

  const topValue = sortedCountries?.reduce((max: number, c) => {
    if (!!c.totalDeathsPerMillion && c.totalDeathsPerMillion > max) {
      max = c.totalDeathsPerMillion;
    }
    return max;
  }, 0);

  const benchmarkValue = topValue ? topValue / 0.6 : 7000;

  return (
    <>
      <CardSubHeading>
        Confirmed deaths per one million population.
      </CardSubHeading>

      <div css={styles.container}>
        <div css={[styles.row, styles.tableHeader]}>
          <div>Countries</div>
          <div css={styles.sortTab} onClick={() => setDesc(!isDesc)}>
            <span css={styles.hideMobile}>Deaths per million</span>
            <span css={styles.hideDesktop}>Deaths/million</span>
            <span
              css={isDesc ? styles.downArrow(color) : styles.upArrow(color)}
            />
          </div>
          <div>Population</div>
          <div>Deaths</div>
        </div>

        <div>
          {sortedCountries?.map((c) => (
            <div key={c.alias} css={styles.countryRow}>
              <div css={[styles.row, styles.contentRow]}>
                <div>
                  <Link
                    to={getCountryUrl(c.alias)}
                    css={styles.countryLink}
                    {...getEventAttributes({
                      category: EventCategory.GlobalSituation,
                      action: "Click - Country name",
                      label: c.displayName,
                    })}
                  >
                    {c.displayName}
                  </Link>
                </div>
                <div>
                  {formatNumber(
                    Math.round((c.totalDeathsPerMillion || 0) * 10) / 10
                  )}
                </div>
                <div>{formatNumberDisplay(c.population || 0)}</div>
                <div css={[{ color }, styles.hideMobile]}>
                  {formatNumber(c.totalDeaths || 0)}
                </div>
                <div css={[{ color }, styles.hideDesktop]}>
                  {formatNumberDisplay(c.totalDeaths || 0)}
                </div>
              </div>
              <div css={styles.barCell}>
                <div
                  css={styles.bar}
                  style={{
                    backgroundColor: color,
                    width: `${formatNumberDisplay(
                      Math.min(
                        1,
                        (c.totalDeathsPerMillion || 0) / benchmarkValue
                      ),
                      true
                    )}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <CardSubHeading>
        For countries with a population over 4 million with over 2,500 cases.
      </CardSubHeading>
    </>
  );
};

export default RankDeathCases;
